import { styles } from 'consts';
import styled from 'styled-components';

const {
  accordionTransition,
  boxShadow,
  selectedBoxShadow,
  white,
  whiteDarker,
  paymentColor,
  promoMainColor,
  paymentDescription,
  fontMBank,
  fontMontserrat,
  grayPrimary,
  opacityTransition,
  progressBarGradient,
  disabledMaskLight,
  promoGradient,
} = styles;

interface IProps {
  isChecked: boolean;
  isDefaultStyle: boolean;
  isDisabled: boolean;
  height?: number;
  hasPromo?: boolean;
}

interface IPromo {
  hasPromo?: boolean;
  isChecked: boolean;
}
const promoMainWidth = '1.5rem';
const promoMainRightIndentValue = '.4rem';

const setMask = ({ isDisabled }: IProps) => (isDisabled ? disabledMaskLight : 'transparent');
const setCursor = ({ isDisabled }: IProps) => (isDisabled ? 'not-allowed' : 'pointer');

const setBackgroundColor = ({ isChecked, isDefaultStyle }: IProps) => {
  if (isDefaultStyle) return white;
  return isChecked ? white : whiteDarker;
};

const setBoxShadow = (p: IProps) =>
  p.isChecked
    ? `0px 2px 8px 0px ${selectedBoxShadow}, 0px 2px 16px 0px ${selectedBoxShadow}`
    : `0 0.125rem 0.5rem 0 ${boxShadow}`;

const showPromo = ({ hasPromo, isChecked }: IPromo) => (hasPromo && !isChecked ? '1' : '0');
const getPromoBackground = ({ hasPromo, isChecked }: IPromo) =>
  hasPromo && !isChecked ? `${promoGradient} border-box` : '#fff';
const getWidth = ({ hasPromo, isChecked }: IPromo) =>
  hasPromo && !isChecked ? `calc(100% - ${promoMainRightIndentValue})` : '100%';
export const AccordionContainer = styled.div<IProps & IPromo>`
  border-radius: 0.5rem;
  margin-bottom: 0.25rem;
  background-color: ${setBackgroundColor};
  box-shadow: ${setBoxShadow};
  padding: 1rem 0;
  position: relative;
  cursor: ${setCursor};
  width: ${getWidth};
  transition: opacity 600ms ease-in-out, width 600ms ease-in-out;

  -webkit-tap-highlight-color: transparent;

  .accordion-item__icon_container-PAYPO {
    img {
      max-width: 90%;
    }
  }

  .accordion-item__icon_container-GOOGLE_PAY {
    right: 0;

    img {
      height: auto;
      width: 5.5rem;
    }
  }

  .accordion-item__icon_container-APPLE_PAY {
    right: 0.25rem;

    img {
      margin-top: 0.5rem;
      max-width: 85%;
      height: auto;
    }
  }

  .accordion-item__icon_container-CLICK_TO_PAY {
    right: 0.75rem;

    @media (max-width: 424px) {
      top: 0;
    }

    img {
      width: 5.5rem;

      @media (max-width: 380px) {
        width: 5rem;
      }
    }
  }

  .accordion-item__content-wrapper-CARD_RECURRING .accordion-item__title {
    white-space: normal;
    overflow: visible;
    min-height: 1.5rem;
    height: auto;
  }

  .accordion-item__content-wrapper-CARD_RECURRING {
    padding-right: 3rem;
  }

  .accordion-item__icon_container-CARD_RECURRING {
    top: 1rem;
    position: absolute;
    right: 0.5rem;

    img {
      height: 2.6rem;
      width: auto;
    }
  }

  .disabled__mask {
    background: ${setMask};
    border-radius: 0.1875rem;
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    width: 100%;
    z-index: 1;
  }
`;

const setHeight = ({ height = 0 }: { height?: number }) => `${height}px`;

export const AccordionAnimation = styled.div<{ height?: number }>`
  overflow: hidden;
  height: 0px;
  height: ${setHeight};
  transition: ${accordionTransition};
`;

export const AccordionContentWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;

export const AccordionContent = styled.div`
  display: flex;
  pointer-events: none;
  padding: 0 1rem;
  width: 100%;

  .description__container {
    width: 100%;
  }

  input {
    height: 1.5rem;
    width: 1rem;
    margin-right: 1.0625rem;
  }
`;

export const AccordionItemDescription = styled.div`
  color: ${paymentDescription};
  height: 1.25rem;
  font-family: ${fontMontserrat};
  font-size: 0.75rem;
  font-weight: normal;
  font-style: normal;
  font-stretch: normal;
  line-height: 2;
  letter-spacing: normal;
  margin-right: 4.25rem;

  @media (min-width: 768px) and (max-width: 940px), (max-width: 450px) {
    padding-top: 0.3rem;
    line-height: 1.3;
    height: 1.5rem;
  }
`;

export const AccordionItemBoldElement = styled.span`
  font-weight: bold;
`;

export const AccordionItemTitle = styled.div`
  width: 100%;
  overflow: hidden;
  white-space: nowrap;
  height: 1.5rem;
  font-size: 1.175rem;
  font-family: ${fontMBank};
  font-weight: normal;
  font-style: normal;
  font-stretch: normal;
  line-height: 1.35;
  letter-spacing: normal;
  color: ${paymentColor};
`;

export const AccordionItemTitlePromo = styled.span`
  color: ${promoMainColor};
`;

export const AccordionPaymentIcon = styled.div`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;

  img {
    height: 3.3125rem;
    width: 4.25rem;
  }
`;

export const AccordionCheckbox = styled.div`
  position: relative;
  height: 1rem;
  width: 1rem;
  margin-right: 1rem;
  margin-top: 0.375rem;
  border-radius: 0.5rem;

  input {
    opacity: 0;
    height: 1rem;
    width: 1rem;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 2;
  }

  input + label {
    background-color: ${grayPrimary};
    display: block;
    padding: 0;
    position: relative;
    height: 1rem;
    width: 1rem;
    border-radius: 50%;
  }

  input + label:before {
    content: '';
    height: 0.625rem;
    width: 0.625rem;
    position: absolute;
    top: 0.1875rem;
    left: 0.1875rem;
    background-image: ${progressBarGradient};
    border-radius: 50%;
    opacity: 0;
    transition: ${opacityTransition};
  }

  input:checked + label:before {
    opacity: 1;
  }
`;

export const AccordionItemPromoElement = styled.div<IPromo>`
  opacity: ${showPromo};
  top: 0;
  right: -${promoMainRightIndentValue};
  width: ${promoMainWidth};
  height: 100%;
  position: absolute;
  background: ${getPromoBackground};
  border-radius: 0 0.5rem 0.5rem 0;
  box-sizing: border-box;
  transition: opacity 800ms ease-in-out, width 800ms ease-in-out;
  z-index: -1;
`;
